import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import Select from '../../../components/ui/Select';

const DisputeModal = ({ isOpen, onClose, transaction, onSubmitDispute }) => {
  const [disputeData, setDisputeData] = useState({
    reason: '',
    description: '',
    attachments: []
  });

  const reasonOptions = [
    { value: 'incorrect_amount', label: 'Montant incorrect' },
    { value: 'missing_commission', label: 'Commission manquante' },
    { value: 'wrong_calculation', label: 'Erreur de calcul' },
    { value: 'attribution_error', label: 'Erreur d\'attribution' },
    { value: 'payment_delay', label: 'Retard de paiement' },
    { value: 'other', label: 'Autre' }
  ];

  const handleInputChange = (field, value) => {
    setDisputeData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e?.target?.files);
    setDisputeData(prev => ({
      ...prev,
      attachments: [...prev?.attachments, ...files]
    }));
  };

  const removeAttachment = (index) => {
    setDisputeData(prev => ({
      ...prev,
      attachments: prev?.attachments?.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSubmitDispute(transaction?.id, disputeData);
    setDisputeData({
      reason: '',
      description: '',
      attachments: []
    });
    onClose();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-background/80 backdrop-blur-sm" onClick={onClose}></div>

        <div className="inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-card border border-border rounded-lg shadow-xl">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-card-foreground">Contester une commission</h3>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <Icon name="X" size={20} />
            </Button>
          </div>

          {transaction && (
            <div className="bg-muted/30 rounded-lg p-4 mb-6">
              <h4 className="font-medium text-card-foreground mb-2">Détails de la transaction</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Date:</span>
                  <span className="ml-2 text-card-foreground">
                    {new Date(transaction.date)?.toLocaleDateString('fr-FR')}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Lieu:</span>
                  <span className="ml-2 text-card-foreground">{transaction?.venue}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Artiste:</span>
                  <span className="ml-2 text-card-foreground">{transaction?.artist}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Commission:</span>
                  <span className="ml-2 text-card-foreground">
                    {transaction?.commission?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                  </span>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Select
                label="Motif de la contestation"
                options={reasonOptions}
                value={disputeData?.reason}
                onChange={(value) => handleInputChange('reason', value)}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-card-foreground mb-2">
                Description détaillée
              </label>
              <textarea
                value={disputeData?.description}
                onChange={(e) => handleInputChange('description', e?.target?.value)}
                placeholder="Décrivez en détail le problème rencontré..."
                rows={4}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-card-foreground mb-2">
                Pièces justificatives
              </label>
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <Icon name="Upload" size={24} className="mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">
                    Cliquez pour télécharger des fichiers ou glissez-déposez
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    PDF, JPG, PNG, DOC jusqu'à 10MB
                  </p>
                </label>
              </div>

              {disputeData?.attachments?.length > 0 && (
                <div className="mt-4 space-y-2">
                  {disputeData?.attachments?.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-muted rounded-md">
                      <div className="flex items-center space-x-2">
                        <Icon name="File" size={16} className="text-muted-foreground" />
                        <span className="text-sm text-card-foreground">{file?.name}</span>
                        <span className="text-xs text-muted-foreground">
                          ({formatFileSize(file?.size)})
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeAttachment(index)}
                        iconName="X"
                      >
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5" />
                <div className="text-sm">
                  <p className="text-warning font-medium mb-1">Information importante</p>
                  <p className="text-muted-foreground">
                    Une fois soumise, votre contestation sera examinée par notre équipe dans un délai de 3-5 jours ouvrables. 
                    Vous recevrez une notification par email avec la décision.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-4 pt-4">
              <Button type="submit" variant="default">
                Soumettre la contestation
              </Button>
              <Button type="button" variant="outline" onClick={onClose}>
                Annuler
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DisputeModal;